/**
 * NDF Settlement Calculator Service
 *
 * This service provides functions for calculating settlements on Non-Deliverable Forward (NDF)
 * FX contracts that settle via compensation (net cash settlement).
 */

export interface NDFCalculationInput {
  direction: string;
  currency1: string;
  quantityCurrency1: number;
  price: number;
  currency2: string;
  quantityCurrency2: number;
  settlementType: string;
  settlementCurrency: string;
  fixingPrice: number;
  settlementPrice: number;
}

export interface NDFCalculationResult {
  settlement: number;
  wePay: number;
  weReceive: number;
  wePayCurrency: string;
  weReceiveCurrency: string;
}

/**
 * Calculate NDF settlement amounts for compensated (netted) FX forwards.
 *
 * @param input - Trade parameters for settlement calculation
 * @returns Settlement calculation results
 * @throws Error if validation fails
 */
export function calculateNDFSettlement(input: NDFCalculationInput): NDFCalculationResult {
  const {
    direction,
    currency1,
    quantityCurrency1,
    price,
    currency2,
    quantityCurrency2,
    settlementType,
    settlementCurrency,
    fixingPrice,
    settlementPrice
  } = input;

  // Validate string inputs are not empty
  if (!direction) {
    throw new Error("Direction cannot be empty");
  }
  if (!currency1) {
    throw new Error("Currency1 cannot be empty");
  }
  if (!currency2) {
    throw new Error("Currency2 cannot be empty");
  }
  if (!settlementType) {
    throw new Error("Settlement type cannot be empty");
  }
  if (!settlementCurrency) {
    throw new Error("Settlement currency cannot be empty");
  }

  // Validate settlement type
  if (settlementType !== "Compensación") {
    throw new Error("Only 'Compensación' settlement type is supported");
  }

  // Validate direction
  if (direction !== "Buy" && direction !== "Sell") {
    throw new Error("Direction must be 'Buy' or 'Sell'");
  }

  // Validate numeric inputs are positive
  if (quantityCurrency1 <= 0) {
    throw new Error(`Quantity currency1 must be greater than zero, got ${quantityCurrency1}`);
  }
  if (price <= 0) {
    throw new Error(`Price must be greater than zero, got ${price}`);
  }
  if (quantityCurrency2 <= 0) {
    throw new Error(`Quantity currency2 must be greater than zero, got ${quantityCurrency2}`);
  }
  if (fixingPrice <= 0) {
    throw new Error(`Fixing price must be greater than zero, got ${fixingPrice}`);
  }
  if (settlementPrice <= 0) {
    throw new Error(`Settlement price must be greater than zero, got ${settlementPrice}`);
  }

  // Calculate settlement
  // Direction multiplier: 1 for Buy, -1 for Sell
  const directionMultiplier = direction === "Buy" ? 1 : -1;

  // Core settlement formula
  const rawSettlement = directionMultiplier * quantityCurrency1 * (fixingPrice - price) / settlementPrice;

  // Round to nearest whole unit for CLP, 2 decimal places for USD/EUR/other
  const decimals = settlementCurrency === 'CLP' ? 0 : 2;
  const settlement = Number(rawSettlement.toFixed(decimals));

  // Calculate WePay and WeReceive
  // Convention: WePay is negative when we pay, WeReceive is positive when we receive
  let wePay: number;
  let weReceive: number;
  let wePayCurrency: string;
  let weReceiveCurrency: string;

  if (settlement >= 0) {
    wePay = 0.0;
    weReceive = settlement;
    wePayCurrency = settlementCurrency;
    weReceiveCurrency = settlementCurrency;
  } else {
    wePay = settlement; // Keep negative value
    weReceive = 0.0;
    wePayCurrency = settlementCurrency;
    weReceiveCurrency = settlementCurrency;
  }

  console.log(
    `NDF Settlement calculated: ${direction} ${quantityCurrency1} ${currency1} @ ${price}, ` +
    `Fixing: ${fixingPrice}, Settlement: ${settlement.toFixed(2)} ${settlementCurrency}`
  );

  return {
    settlement,
    wePay,
    weReceive,
    wePayCurrency,
    weReceiveCurrency
  };
}
